import React, { useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { makeStyles } from '@material-ui/styles'
import {
  Card,
  CardActions,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Typography,
  Switch
} from '@material-ui/core'

import { numberFormat } from 'helpers'
import { ExModalDelete } from 'components'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import DonutLargeIcon from '@material-ui/icons/DonutLarge'

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    padding: 0
  },
  inner: {
    minWidth: 1000
  },
  productCell: {
    display: 'flex',
    alignItems: 'center'
  },
  actions: {
    justifyContent: 'flex-end'
  },
  updateButton: {
    marginRight: theme.spacing(1)
  },
  imageContainer: {
    height: 50,
    width: 50,
    minWidth: 50,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '5px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(1.5)
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  discountContainer: {
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.5),
    },
  },
  chip: {
    display: 'flex',
    alignItems: 'center'
  },
  stockContainer: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap'
  },
  stockAvailable: {
    color: '#4caf50',
    fontWeight: 600,
    marginLeft: theme.spacing(0.5)
  },
  stockEmpty: {
    color: '#f44336',
    fontWeight: 600,
    marginLeft: theme.spacing(0.5)
  },
  descriptionCell: {
    maxWidth: 220,
    wordBreak: 'break-word'
  },
  descriptionText: {
    color: '#000000',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}))

const ExTable = props => {
  const { 
    className, 
    products, 
    page, 
    pages, 
    limit, 
    onChangePage, 
    onChangeRowsPerPage, 
    onDeleteData, 
    onUpdateData, 
    onChangeStock,
    ...rest 
  } = props

  const [modal, setModal] = useState(false)
  const [selectedId, setSelectedId] = useState(0)
  const [localStock, setLocalStock] = useState({})

  const classes = useStyles()

  const handleCloseModal = () => {
    setModal(false)
  }

  const handleOpenModal = (id) => {
    setSelectedId(id)
    setModal(true)
  }

  const handleDeleteModal = () => {
    onDeleteData(selectedId)
    setModal(false)
  }

  const handleChangeStock = async (productId, currentStock, nextChecked) => {
    setLocalStock(prev => ({
      ...prev,
      [productId]: nextChecked
    }))

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${process.env.REACT_APP_ADMIN_URL}product/has-stock/${productId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ has_stock: Boolean(nextChecked) })
      })
      const result = await response.json()

      if (!result.status) {
        setLocalStock(prev => ({
          ...prev,
          [productId]: currentStock
        }))
      }

      if (onChangeStock) {
        onChangeStock(productId, nextChecked, result)
      }
    } catch (err) {
      setLocalStock(prev => ({
        ...prev,
        [productId]: currentStock
      }))
    }
  }

  return (
    <Card
      {...rest}
      className={clsx(classes.root, className)}
    >
      <CardContent className={classes.content}>
        <PerfectScrollbar>
          <div className={classes.inner}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>No</TableCell>
                  <TableCell>Produk</TableCell>
                  <TableCell>Stok</TableCell>
                  <TableCell>Kategori / Produsen</TableCell>
                  <TableCell>Diskon</TableCell>
                  <TableCell className={classes.descriptionCell}>Deskripsi</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product, index) => {
                  const defaultStock = Boolean(
                    product.has_stock === 1 ||
                    product.has_stock === true ||
                    product.has_stock === '1' ||
                    product.has_stock === 'true' ||
                    product.hasStock === 1 ||
                    product.hasStock === true ||
                    product.hasStock === '1' ||
                    product.hasStock === 'true'
                  )
                  const hasStock = localStock[product.id] !== undefined
                    ? localStock[product.id]
                    : defaultStock

                  return (
                    <TableRow
                      className={classes.tableRow}
                      hover
                      key={product.id}
                    >
                      <TableCell>{((page*limit)-limit) + (index+1)}</TableCell>
                      <TableCell>
                        <div className={classes.productCell}>
                          {product.image ? (
                            <div className={classes.imageContainer}>
                              <img
                                key={product.image}
                                alt={product.name}
                                className={classes.image}
                                src={process.env.REACT_APP_IMAGE_URL + product.image}
                              />
                            </div>
                          ) : null}
                          <div>
                            <Typography variant="body1" style={{ fontWeight: 500, color: '#000000' }}>
                              {product.name}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#000000' }}>
                              <span style={{ fontWeight: 600 }}>Harga:</span> {numberFormat(product.price, 'Rp. ')}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#000000' }}>
                              <span style={{ fontWeight: 600 }}>Berat:</span> {product.weight}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#000000' }}>
                              <span style={{ fontWeight: 600 }}>Jumlah:</span> {product.amount + " /Karton"}
                            </Typography>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={classes.stockContainer}>
                          <Switch
                            checked={hasStock}
                            onChange={(e) => handleChangeStock(product.id, hasStock, e.target.checked)}
                            color="primary"
                          />
                          <Typography
                            className={hasStock ? classes.stockAvailable : classes.stockEmpty}
                            variant="body2"
                          >
                            {hasStock ? 'Ada' : 'Habis'}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ color: '#000000' }}>
                          <span style={{ fontWeight: 600 }}>Kategori :</span> {product.category?.name || '-'}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#000000' }}>
                          <span style={{ fontWeight: 600 }}>Produsen:</span> {product.producer?.name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>  
                        <div className={classes.discountContainer}>
                          <Chip
                            label="SGH"
                            icon={
                              <div className={classes.chip}>
                                <DonutLargeIcon />
                                <Typography>{numberFormat(product.discount?.sgh, '')}%</Typography>
                            </div>
                            }
                          />
                          <Chip
                            label="A+"
                            icon={
                              <div className={classes.chip}>
                                <DonutLargeIcon />
                                <Typography>{numberFormat(product.discount?.grade_aplus, '')}%</Typography>
                            </div>
                            }
                          />
                          <Chip
                            label="A"
                            icon={
                              <div className={classes.chip}>
                                <DonutLargeIcon />
                                <Typography>{numberFormat(product.discount?.grade_a, '')}%</Typography>
                            </div>
                            }
                          />
                          <Chip
                            label="B+"
                            icon={
                              <div className={classes.chip}>
                                <DonutLargeIcon />
                                <Typography>{numberFormat(product.discount?.grade_bplus, '')}%</Typography>
                            </div>
                            }
                          />
                        </div>
                        <div className={classes.discountContainer}>
                          <Chip
                            label="B"
                            icon={
                              <div className={classes.chip}>
                                <DonutLargeIcon />
                                <Typography>{numberFormat(product.discount?.grade_b, '')}%</Typography>
                            </div>
                            }
                          />
                          <Chip
                            label="C"
                            icon={
                              <div className={classes.chip}>
                                <DonutLargeIcon />
                                <Typography>{numberFormat(product.discount?.grade_c, '')}%</Typography>
                            </div>
                            }
                          />
                          <Chip
                            label="Eceran"
                            icon={
                              <div className={classes.chip}>
                                <DonutLargeIcon />
                                <Typography >{numberFormat(product.discount?.retail, '')}%</Typography>
                            </div>
                            }
                          />
                        </div>
                      </TableCell>
                      <TableCell className={classes.descriptionCell}>
                        <Typography className={classes.descriptionText}>
                          {product.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          className={classes.updateButton}
                          color="inherit"
                          onClick={() => onUpdateData(product.id)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="inherit"
                          onClick={() => handleOpenModal(product.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </PerfectScrollbar>
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          component="div"
          count={pages * limit}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          page={page-1}
          rowsPerPage={limit}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </CardActions>
      
      <ExModalDelete 
        open={modal} 
        onClose={handleCloseModal} 
        onDelete={handleDeleteModal}
      />
    </Card>
  )
}

ExTable.propTypes = {
  className: PropTypes.string,
  products: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeRowsPerPage: PropTypes.func.isRequired,
  onDeleteData: PropTypes.func.isRequired,
  onUpdateData: PropTypes.func.isRequired,
  onChangeStock: PropTypes.func
}

export default ExTable

